import React from 'react';
import { Box, useTheme } from '@mui/material';
import { Chip } from '../Chip';
import { TitleField } from '../TitleField';
import { Typography } from '../Typography';

export interface DetailsSubheaderProps {
  /**
   * The main title text
   */
  title: string;
  /**
   * Optional subtitle text displayed below the title
   */
  subtitle?: string;
  /**
   * Display a chip with a number next to the title
   */
  titleCount?: number;
  /**
   * Optional slot for buttons and controls on the right side (above 600px)
   * or below the title/subtitle (below 600px)
   */
  controls?: React.ReactNode;
  /**
   * Optional test ID for the component
   */
  testId?: string;
  /**
   * Optional editable title configuration
   * When provided, renders a TitleField instead of static text
   */
  editableTitle?: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  };
}

/**
 * A responsive subheader component for detail pages that displays a title and subtitle
 * on the left, with optional controls on the right. Below 600px, the title/subtitle
 * takes full width and controls move to the next line. Above 600px, the title/subtitle
 * takes 2/3 of the width and controls take 1/3.
 */
export const DetailsSubheader: React.FC<DetailsSubheaderProps> = ({
  title,
  subtitle,
  controls,
  testId,
  titleCount,
  editableTitle,
}) => {
  const theme = useTheme();
  const hasTitleCount = titleCount !== undefined && titleCount !== null;

  return (
    <Box
      data-testid={testId ? `details-subheader-${testId}` : 'details-subheader'}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.custom.spacing?.lg || 2,
        width: '100%',
      }}
    >
      {/* Left side: Title and Subtitle */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.custom.spacing?.xs || 0.5,
          flex: {
            xs: '1 1 100%',
            sm: '2 1 0',
          },
          minWidth: 0,
        }}
      >
        <Box display="flex" alignItems="center">
          {editableTitle ? (
            <TitleField
              value={editableTitle.value}
              onChange={editableTitle.onChange}
              onBlur={editableTitle.onBlur}
              size="small"
            />
          ) : (
            <Typography component="h2" sx={{ fontSize: '18px', fontWeight: '600', color: '#181D27' }}>
              {title}
            </Typography>
          )}
          {hasTitleCount && (
            <Box data-testid="title-count" sx={{ ml: 3 }}>
              <Chip label={titleCount} variant="filled" size="medium" />
            </Box>
          )}
        </Box>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: '20px', color: '#535862', ml: editableTitle ? 2 : 0 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Right side: Controls */}
      {controls && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.custom.spacing?.md || 1,
            flex: {
              xs: '1 1 100%',
              sm: '1 1 0',
            },
            minWidth: 0,
            justifyContent: {
              xs: 'flex-start',
              sm: 'flex-end',
            },
          }}
        >
          {controls}
        </Box>
      )}
    </Box>
  );
};
